import React, {
    createContext,
    useEffect,
    useContext,
    useCallback,
    memo,
} from 'react';
import type {
    REACT_NATIVE_RECEIVED_MESSAGE_TYPES,
    REACT_NATIVE_SENT_MESSAGE_TYPES,
} from '@utils/constants';
import { goBack } from '@utils/navigation';
import { useReactNativeNavigationContextAtom } from '@states/navigation';

export type IReactNativeContext = {
    isReactNative: boolean;
    platform?: 'ios' | 'android';
    sendMessage: (message: ReactNativeSentMessage) => void;
};

export type ReactNativeProviderProps = {
    children: React.ReactNode;
};

export type ReactNativeReceivedMessage = {
    type: keyof typeof REACT_NATIVE_RECEIVED_MESSAGE_TYPES;
};

export type ReactNativeSentMessage = {
    type: keyof typeof REACT_NATIVE_SENT_MESSAGE_TYPES;
    data?: string;
};

const ReactNativeContext = createContext<IReactNativeContext>({
    isReactNative: false,
    platform: undefined,
    sendMessage: () => {},
});

const ReactNativeProvider = ({ children }: ReactNativeProviderProps) => {
    const {
        value: navigationContext,
        reset: resetNavigationContext,
    } = useReactNativeNavigationContextAtom();
    const reactNativeWebView = window.ReactNativeWebView;
    const isReactNative = Boolean(reactNativeWebView);

    const sendMessage = (message: ReactNativeSentMessage): void => {
        if (!isReactNative) return;
        reactNativeWebView?.postMessage(JSON.stringify(message));
    };

    const onReactNativeMessage = useCallback(
        (message: ReactNativeReceivedMessage) => {
            switch (message.type) {
                case 'GO_BACK':
                    if (navigationContext) {
                        navigationContext();
                        resetNavigationContext();
                        break;
                    }
                    void goBack();
                    break;
                default:
                    console.warn(
                        'unhandled message sent from react native:',
                        message,
                    );
            }
        },
        [navigationContext, resetNavigationContext],
    );

    useEffect(() => {
        // Attach the message listener to the window obj
        const ReactNativeMessageHandler = { postMessage: onReactNativeMessage };
        window.ReactNativeMessageHandler = ReactNativeMessageHandler;

        return () => {
            window.ReactNativeMessageHandler = undefined;
        };
    }, [onReactNativeMessage]);

    const context = {
        isReactNative,
        sendMessage,
        platform: window?.injected?.platform,
    };

    return (
        <ReactNativeContext.Provider value={context}>
            {children}
        </ReactNativeContext.Provider>
    );
};

export const useReactNative = (): IReactNativeContext => {
    const context = useContext(ReactNativeContext);

    if (!context) {
        throw new Error(
            'ReactNativeContext must be used inside a ReactNativeContext.Provider',
        );
    }

    return context;
};

export default memo(ReactNativeProvider);
